/*
  <capra-product-form>: the buy bar pinned to the bottom of the product page.
  Without this script the bar still works: each option button opens its panel
  (popovertarget), and the form posts to /cart/add with the variant chosen in
  the <noscript> list. With it:
    - an option button opens the panel with only its own values, aligned with
      it (phones: one button opens every option); choosing a value closes it;
    - choosing a value re-renders the price, the ship line, the option values
      and the buy button from the server (Section Rendering API), so facts
      always come from Shopify, never from a copy kept in the browser;
    - "Yes Please" for debossing opens the text field, required, in capitals,
      with the characters left, and says at once when the studio lacks a
      stamp for a character (or enough of them);
    - Add to bag dims at once, adds through /cart/add.js, then the button turns to the accent with
      "Added" and a check, and a soft light passes through the label ('capra:added' tells the header bag). If it
      fails it says so and nothing is left half done.
    - the name and price are light while a photo is behind them;
    - on a Desk Pad Organizer, "Custom size" (snippets/deskpad-custom) hides the other options but debossing,
      opens length × width, prices the band of the colour's custom desk pad that covers it and adds that band
      with the size as a line property, as the custom pad's own page does (capra-deskpad.js).
*/
import { areaSqIn, bandFor, sizeProperty, stitched } from './capra-deskpad-math.js';
import { Ladder } from './capra-deskpad-ladder.js';

// Markets live under a folder (/en-de/): never fetch a fixed root path.
const ROOT = window.Shopify?.routes?.root || '/';

// Phones: the panel spans the bar. Wider: it hangs from the Customization button.
const PHONE = matchMedia('(max-width: 799px)');
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const SWAPS = ['price', 'status', 'picker', 'ship', 'add'];

class CapraProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.section = this.parentElement.closest('[data-section-id]') || document; // the page, not this element (it carries the id too)
    this.picker = this.querySelector('[data-picker]');
    this.input = this.form.querySelector('[data-variant-input]');
    if (this.input) this.input.disabled = false;
    this.which = null;
    this.customOn = false;

    this.form.addEventListener('change', (event) => this.onChange(event));
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.form.addEventListener('input', (event) => this.onType(event));
    this.addEventListener('click', (event) => this.onPick(event));
    // The unit switch in a custom size is the site's (capra-units): it has set the unit by the time this runs.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-custom-panel] [data-unit]')) queueMicrotask(() => this.customUpdate());
    });
    // Manual, not the browser's light dismiss: that closed the panel on the touch that pressed Add to bag, and
    // iPhone Safari then dropped the tap. A press outside the bar, or Escape, closes it here; the bar's own
    // buttons close it themselves. A dialog opened from the panel (Learn more) keeps both for itself.
    if (this.picker) this.picker.popover = 'manual';
    document.addEventListener('pointerdown', (event) => {
      if (this.isOpen() && !this.contains(event.target) && !event.target.closest?.('dialog')) this.picker.hidePopover();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen() && !document.querySelector('dialog[open]')) this.picker.hidePopover();
    });
    this.picker?.addEventListener('toggle', (event) => this.onToggle(event));
    addEventListener('resize', () => this.isOpen() && this.place());
    // The keyboard rising or falling, or iOS scrolling the page to the field, moves the visible bottom.
    for (const type of ['resize', 'scroll']) {
      window.visualViewport?.addEventListener(type, () => {
        if (!this.isOpen()) return;
        this.place();
        if (this.picker.contains(document.activeElement)) this.revealDeboss('auto');
      });
    }
    // Arrows walk a radiogroup and choose as they go: landing on "Yes Please" there must not pull focus out.
    this.addEventListener('keydown', (event) => { this.arrowed = event.key.startsWith('Arrow'); });
    this.addEventListener('pointerdown', () => { this.arrowed = false; });
    this.syncDebossing();
    this.settle();
  }

  isOpen() {
    return Boolean(this.picker?.matches(':popover-open'));
  }

  /* --- The option panel --- */

  onPick(event) {
    const button = event.target.closest('[data-pick]');
    if (!button || !this.picker) return;
    event.preventDefault();
    const which = 'all';
    if (this.isOpen() && this.which === which) {
      this.picker.hidePopover();
      return;
    }
    this.open(which, button);
  }

  open(which, button) {
    this.which = which;
    this.opener = button || this.querySelector(`[data-pick="${which}"]`);
    this.only();
    this.place();
    if (!this.isOpen()) this.picker.showPopover();
    (this.picker.querySelector('.picker__group:not([hidden]) .picker__input:checked') ||
      this.picker.querySelector('.picker__group:not([hidden]) .picker__input:not(:disabled)'))?.focus({ preventScroll: true });
  }

  // Show only the opened option's values (and the debossing text with its option).
  only() {
    if (!this.picker) return;
    const size = this.picker.querySelector('[data-custom]')?.closest('[data-group]')?.dataset.group;
    for (const group of this.picker.querySelectorAll('.picker__group')) {
      group.hidden = (this.which !== 'all' && group.dataset.group !== this.which) ||
        (this.customOn && group.dataset.group !== size && group.dataset.group !== this.debossGroup());
    }
    const text = this.picker.querySelector('[data-deboss]');
    if (text) text.hidden = !this.debossWanted() || (this.which !== 'all' && text.dataset.group !== this.which);
  }

  // Rises from the bar and is exactly as wide as its tray; phones span the bar.
  // Measured from the bar's own fixed box, never innerHeight: on iOS innerHeight shrinks while the keyboard
  // is up but the bar does not move, and the panel then came down over the buttons and stayed there.
  place() {
    const bar = this.form.getBoundingClientRect();
    const style = this.picker.style;
    // The on-screen keyboard covers the bar without moving it: the panel then rests on the visible bottom
    // (visualViewport) instead, so the field being typed in stays above the keyboard.
    const view = window.visualViewport;
    const floor = this.getBoundingClientRect().bottom;
    const rest = Math.min(bar.top, view ? view.offsetTop + view.height : bar.top);
    style.setProperty('--picker-bottom', `${floor - rest + 8}px`);
    if (PHONE.matches) {
      style.removeProperty('--picker-left');
      style.removeProperty('--picker-width');
      return;
    }
    style.setProperty('--picker-left', `${bar.left}px`);
    style.setProperty('--picker-width', `${bar.width}px`);
  }

  onToggle(event) {
    const open = event.newState === 'open';
    for (const button of this.querySelectorAll('[data-pick]')) {
      button.setAttribute('aria-expanded', String(open && button === this.opener));
    }
    if (!open) {
      if (this.picker.contains(document.activeElement)) this.opener?.focus({ preventScroll: true });
      this.which = null;
    }
  }

  /* --- Choosing a value --- */

  async onChange(event) {
    if (event.target.closest('[data-custom-panel]')) return this.customUpdate();
    if (!event.target.matches('.picker__input')) return;
    const choice = event.target;
    if (choice.matches('[data-custom]')) return this.setCustom(true);
    if (this.customOn) {
      // Debossing stays a choice of the custom pad; any other size leaves it.
      if (choice.closest('[data-group]')?.dataset.group === this.debossGroup()) {
        this.syncDebossing();
        this.customUpdate();
        if (this.debossWanted()) this.focusDeboss();
        return;
      }
      this.setCustom(false);
    }
    this.syncDebossing();
    const group = choice.closest('[data-group]')?.dataset.group;
    // "Yes Please": the box takes focus now, inside the tap, or iOS does not raise the keyboard. It sits
    // outside the redrawn part of the panel, so the redraw below leaves it, its focus and its text alone.
    const deboss = this.debossWanted() && group === this.debossGroup() && !this.arrowed;
    if (deboss) this.focusDeboss();
    const id = choice.dataset.variantId;
    if (!id) return;
    if (this.input) this.input.value = id;
    this.abort?.abort();
    this.abort = new AbortController();
    try {
      const url = this.dataset.productUrl;
      const response = await fetch(`${url}?variant=${id}&section_id=${this.dataset.sectionId}`, { signal: this.abort.signal });
      if (!response.ok) return;
      const fresh = new DOMParser().parseFromString(await response.text(), 'text/html');
      for (const name of SWAPS) {
        const next = fresh.querySelector(`[data-swap="${name}"]`);
        const current = this.section.querySelector(`[data-swap="${name}"]`);
        if (next && current) current.replaceWith(next);
      }
      history.replaceState(null, '', `${url}?variant=${id}`);
    } catch (error) {
      if (error.name !== 'AbortError') throw error;
      return;
    }
    this.syncDebossing();
    if (!this.isOpen()) return;
    this.only();
    if (deboss) {
      // Already focused: only a focus lost to the redraw is given back, never one the visitor moved on purpose.
      // The redraw may have cut the scroll short or moved the box: a no-op when it is still in view.
      const field = this.form.querySelector('.deboss__input');
      if (field && (!document.activeElement || document.activeElement === document.body)) field.focus({ preventScroll: true });
      this.revealDeboss('auto');
    } else if (this.which === 'all') {
      this.picker.querySelector(`[data-group="${group}"] .picker__input:checked`)?.focus({ preventScroll: true });
    } else {
      this.picker.hidePopover();
    }
  }

  /* --- A custom size (Desk Pad Organizer) --- */

  setCustom(on) {
    const panel = this.picker?.querySelector('[data-custom-panel]');
    if (!panel) return;
    this.customOn = on;
    panel.hidden = !on;
    // The organizer's "+ $25" is not what debossing adds to a custom pad, and in another currency Shopify rounds each
    // band on its own: no one figure is true for every size, so the row says none and the panel's price is the sum.
    const yes = [...this.picker.querySelectorAll(`[data-group="${this.debossGroup()}"] .picker__input`)].find((i) => /^yes/i.test(i.value));
    const meta = yes?.nextElementSibling?.querySelector('.picker__meta');
    if (meta) {
      meta.dataset.was ??= meta.textContent;
      meta.textContent = on ? '' : meta.dataset.was;
    }
    this.only();
    if (!on) return;
    this.customUpdate();
    panel.querySelector('[data-custom-length]')?.focus({ preventScroll: true });
  }

  customUpdate() {
    const panel = this.picker?.querySelector('[data-custom-panel]');
    if (!panel || !this.customOn) return;
    if (panel !== this.ladderOf) {
      this.ladderOf = panel;
      this.ladder = new Ladder(panel.querySelector('[data-custom-ladder]'), panel);
      this.rule = JSON.parse(panel.querySelector('[data-custom-rule]').textContent);
    }
    const deboss = this.debossWanted();
    if (!this.ladder.has(deboss)) {
      // The debossing ladder arrives once; until then nothing can be added at a price not yet shown.
      this.custom = null;
      this.form.querySelector('[data-add]')?.setAttribute('disabled', '');
      this.ladder.load(deboss).then(() => this.customUpdate(), () => {});
      return;
    }
    // The site's units (capra-units.js, the size tables' toggle): a switch here converts what is typed.
    const unit = document.documentElement.dataset.units === 'imperial' ? 'in' : 'cm';
    const fields = [panel.querySelector('[data-custom-length]'), panel.querySelector('[data-custom-width]')];
    if (this.customUnit && this.customUnit !== unit) {
      const factor = unit === 'cm' ? 2.54 : 1 / 2.54;
      for (const field of fields) {
        if (field.value) field.value = String(Math.round(parseFloat(field.value) * factor * 10) / 10);
      }
    }
    this.customUnit = unit;
    for (const suffix of panel.querySelectorAll('[data-custom-suffix]')) suffix.textContent = unit;
    const [length, width] = fields.map((field) => parseFloat(field.value));
    const valid = length > 0 && width > 0;
    const max = valid ? bandFor(areaSqIn(length, width, unit), this.ladder.areas) : undefined;
    const band = max === undefined ? undefined : this.ladder.band(max, deboss);
    const long = valid && stitched(length, width, unit, this.rule.side_stitch_over_cm);
    panel.querySelector('[data-note="large"]').hidden = !(valid && max === undefined);
    panel.querySelector('[data-note="stitched"]').hidden = !long || max === undefined;
    this.custom = band && { band, size: sizeProperty(length, width, unit), long };
    const price = this.picker.querySelector('.picker__price');
    if (price) price.textContent = band ? band.p : '';
    const button = this.form.querySelector('[data-add]');
    if (button) button.disabled = !band;
  }

  // What the bag gets for a custom size: the band, the size for the studio, and the debossing text.
  customBody() {
    const panel = this.picker.querySelector('[data-custom-panel]');
    const body = new FormData();
    body.append('id', this.custom.band.i);
    body.append('quantity', '1');
    body.append('properties[Size]', this.custom.size);
    if (this.custom.long) body.append('properties[Construction]', panel.querySelector('[data-custom-construction]').value);
    const text = this.form.querySelector('.deboss__input');
    if (text && !text.disabled && text.value.trim()) body.append('properties[Debossing]', text.value.trim());
    return body;
  }

  /* --- Debossing --- */

  debossGroup() {
    return this.picker?.querySelector('[data-deboss]')?.dataset.group;
  }

  debossWanted() {
    const group = this.debossGroup();
    if (group == null) return false;
    const choice = this.picker.querySelector(`[data-group="${group}"] .picker__input:checked`);
    return Boolean(choice && /^yes/i.test(choice.value));
  }

  syncDebossing() {
    const field = this.form.querySelector('.deboss__input');
    if (!field) return;
    const wanted = this.debossWanted();
    field.required = wanted;
    field.disabled = !wanted; // not posted unless debossing is chosen
    this.only();
    this.count(field);
  }

  count(field) {
    const out = this.form.querySelector('[data-deboss-count]');
    if (out) out.textContent = field.maxLength - field.value.length;
    this.checkStamps(field);
  }

  // The studio has so many stamps of each character: a text asking for one it lacks, or for
  // more than it has, says so at once and cannot go to the bag.
  checkStamps(field) {
    const box = field.closest('[data-deboss]');
    const out = box?.querySelector('[data-deboss-error]');
    if (!box?.dataset.stamps || !out) return;
    this.stamps ||= JSON.parse(box.dataset.stamps);
    const used = {};
    let message = '';
    for (const char of field.value) {
      used[char] = (used[char] || 0) + 1;
      const shown = char === ' ' ? '“ ”' : `“${char}”`;
      if (!(char in this.stamps)) { message = box.dataset.missing.replace('__char__', shown); break; }
      if (used[char] > this.stamps[char]) {
        message = box.dataset.short.replace('__count__', this.stamps[char]).replace('__char__', shown);
        break;
      }
    }
    field.setCustomValidity(message);
    field.toggleAttribute('aria-invalid', Boolean(message));
    if (out.textContent === message) return;
    out.textContent = message;
    if (message) this.revealDeboss('auto'); // the error line pushes the note down: keep both in sight
  }

  onType(event) {
    if (event.target.closest('[data-custom-panel]')) return this.customUpdate();
    const field = event.target;
    if (!field.matches('.deboss__input')) return;
    const at = field.selectionStart;
    field.value = field.value.toUpperCase();
    field.setSelectionRange(at, at);
    this.count(field);
  }

  // The field opens below the last choice: focus it and bring it into view in one move
  focusDeboss() {
    const field = this.form.querySelector('.deboss__input');
    if (!field) return;
    field.focus({ preventScroll: true });
    this.revealDeboss(REDUCED.matches ? 'auto' : 'smooth');
  }

  // The box and what appears under it (the error, the final-sale note): as little scroll as that takes,
  // never so much that the field goes under the panel's sticky title.
  revealDeboss(behavior) {
    const box = this.form.querySelector('[data-deboss]');
    if (!box || box.hidden || !this.isOpen()) return;
    requestAnimationFrame(() => {
      const panel = this.picker.getBoundingClientRect();
      const top = this.picker.querySelector('.picker__top')?.getBoundingClientRect().bottom ?? panel.top;
      const { top: start, bottom: end } = box.getBoundingClientRect();
      const by = Math.min(end + 12 - panel.bottom, start - top - 12);
      if (by > 0 || start < top) this.picker.scrollBy({ top: start < top ? start - top - 12 : by, behavior });
    });
  }

  /* --- Add to bag --- */

  async onSubmit(event) {
    event.preventDefault();
    const field = this.form.querySelector('.deboss__input');
    // Debossing chosen but no text yet: open the panel at the text box.
    if (field && field.required && (!field.value.trim() || !field.checkValidity())) {
      this.open('all');
      this.focusDeboss();
      return;
    }
    // A custom size without a size yet (or past the ladder): open the panel at the fields.
    if (this.customOn && !this.custom) {
      this.open('all');
      this.picker.querySelector('[data-custom-length]')?.focus({ preventScroll: true });
      return;
    }
    const button = this.form.querySelector('[data-add]');
    const message = this.form.querySelector('[data-message]');
    if (!button || button.disabled) return;
    if (this.isOpen()) this.picker.hidePopover();
    button.classList.remove('is-added');
    button.classList.add('is-adding');
    button.setAttribute('aria-busy', 'true');
    message.hidden = true;
    message.classList.remove('is-error');
    try {
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: this.customOn ? this.customBody() : new FormData(this.form),
      });
      // Shopify's own words when it refuses (a stock limit); anything else, even a page that is not JSON, is the plain message.
      if (!response.ok) throw Object.assign(new Error('add failed'), { shown: (await response.json().catch(() => ({}))).description });
      // The light starts the moment the server says yes; the count follows on its own.
      this.added(button);
      fetch(`${ROOT}cart.js`).then((r) => r.json()).then((cart) => {
        for (const count of document.querySelectorAll('[data-cart-count]')) {
          count.textContent = cart.item_count;
          count.hidden = cart.item_count === 0;
        }
      }).catch(() => {});
      message.textContent = this.dataset.added; // read out, not shown: the bar and the bag say it
      message.hidden = false;
    } catch (error) {
      message.textContent = error.shown || this.dataset.failed; // never a script's error text
      message.classList.add('is-error');
      message.hidden = false;
    } finally {
      button.classList.remove('is-adding');
      button.removeAttribute('aria-busy');
    }
  }

  // "Added" starts to be written at about 0.3s; the header bag fills from then, at the same pace.
  added(button) {
    button.classList.add('is-added');
    setTimeout(() => button.classList.remove('is-added'), 2200);
    navigator.vibrate?.(14); // a small tap where the phone allows it
    document.dispatchEvent(new CustomEvent('capra:added', { detail: { handoff: REDUCED.matches ? 0 : 300 } }));
  }

  /* --- One settle between the photos and the description --- */

  // Wide screens: the photos fill the window from its top edge. A scroll that stops between them
  // and the description finishes the move in the direction it was going (owner, 2026-10-16: a
  // subtle snap): down, the description meets the top edge and the header is gone; up, the photos
  // are whole again. Nowhere else does anything pull: the browser's scroll snap held the page back
  // on every scroll down, so it is not used.
  settle() {
    const photos = this.section.querySelector('.pdp__media');
    const info = this.section.querySelector('.pdp__info');
    if (!photos || !info) return;
    let last = scrollY;
    let down = true;
    let timer;
    const finish = () => {
      if (PHONE.matches || document.documentElement.classList.contains('is-locked')) return;
      const from = Math.max(0, photos.getBoundingClientRect().top + scrollY);
      const to = info.getBoundingClientRect().top + scrollY;
      const y = scrollY;
      if (y <= from + 1 || y >= to - 1) return;
      scrollTo({ top: down ? to : from, behavior: REDUCED.matches ? 'instant' : 'smooth' });
    };
    addEventListener('scroll', () => {
      down = scrollY >= last;
      last = scrollY;
      clearTimeout(timer);
      timer = setTimeout(finish, 140); // scrollend, where the browser does not send it
    }, { passive: true });
  }
}

customElements.define('capra-product-form', CapraProductForm);
